import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { User } from '../types'
import { hasPermission, isSuperAdmin } from '../lib/permissions'
import {
  navPreferencesService,
  applyNavOrder,
  emptyNavPreferences,
  NavPreferences,
} from '../services/navPreferencesService'
import NotificationBell from './NotificationBell'
import './Sidebar.css'
import { toast } from './Toast'

interface SidebarProps {
  currentView: string
  onViewChange: (view: string) => void
  user: User
  onLogout: () => void
  isOpen: boolean
  onToggle: () => void
}

type NavItem = {
  id: string
  label: string
  icon: string
  perm: string | null
  section: string
  base?: boolean
}

/* Enterprise monoline icon set (Lucide-style) — single stroke weight,
   inherits the nav colour via currentColor. */
const ICONS: Record<string, JSX.Element> = {
  car: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 17h14M4 17v-4l2-5h12l2 5v4M4 17v2M20 17v2" />
      <circle cx="7.5" cy="17" r="1.6" />
      <circle cx="16.5" cy="17" r="1.6" />
    </svg>
  ),
  today: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="9" rx="1.5" />
      <rect x="14" y="3" width="7" height="5" rx="1.5" />
      <rect x="14" y="11" width="7" height="10" rx="1.5" />
      <rect x="3" y="15" width="7" height="6" rx="1.5" />
    </svg>
  ),
  search: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
  pipeline: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <line x1="9" y1="4" x2="9" y2="20" />
      <line x1="15" y1="4" x2="15" y2="20" />
    </svg>
  ),
  contracts: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
      <line x1="9" y1="13" x2="15" y2="13" />
      <line x1="9" y1="17" x2="13" y2="17" />
    </svg>
  ),
  shield: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l7 3v5c0 4.6-3.1 7.7-7 9-3.9-1.3-7-4.4-7-9V6z" />
      <path d="M9 12l2 2 4-4.5" />
    </svg>
  ),
  card: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2.5" y="6" width="19" height="12" rx="2" />
      <circle cx="12" cy="12" r="2.4" />
      <line x1="6" y1="12" x2="6.01" y2="12" />
      <line x1="18" y1="12" x2="18.01" y2="12" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M8.3 12.4l2.5 2.5 4.9-5.4" />
    </svg>
  ),
  receipt: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 3l1.5 1.2L9 3l1.5 1.2L12 3l1.5 1.2L15 3l1.5 1.2L18 3v18l-1.5-1.2L15 21l-1.5-1.2L12 21l-1.5-1.2L9 21l-1.5-1.2L6 21z" />
      <line x1="9" y1="9" x2="15" y2="9" />
      <line x1="9" y1="13" x2="15" y2="13" />
    </svg>
  ),
  bell: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9a6 6 0 0 1 12 0c0 5 2.2 6 2.2 6H3.8S6 14 6 9z" />
      <path d="M10.4 20a1.7 1.7 0 0 0 3.2 0" />
    </svg>
  ),
  coins: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="9" cy="7" rx="6" ry="3.2" />
      <path d="M3 7v4c0 1.8 2.7 3.2 6 3.2s6-1.4 6-3.2V7" />
      <path d="M9 14.2v3c0 1.8 2.7 3.2 6 3.2s6-1.4 6-3.2v-6" />
      <ellipse cx="15" cy="11.2" rx="6" ry="3.2" />
    </svg>
  ),
  inbox: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="21 12 16 12 14.5 15 9.5 15 8 12 3 12" />
      <path d="M5.6 5.3 3 12v6a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-6l-2.6-6.7A2 2 0 0 0 16.6 4H7.4a2 2 0 0 0-1.8 1.3z" />
    </svg>
  ),
  chart: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="21" x2="4" y2="13" />
      <line x1="10" y1="21" x2="10" y2="4" />
      <line x1="16" y1="21" x2="16" y2="9" />
      <line x1="3" y1="21" x2="21" y2="21" />
    </svg>
  ),
  message: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H8l-4 3.5V5a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2z" />
      <line x1="8" y1="9" x2="16" y2="9" />
      <line x1="8" y1="12.5" x2="13" y2="12.5" />
    </svg>
  ),
  gear: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1" />
    </svg>
  ),
}

/** The canonical order, used until a user saves one of their own. */
const NAV_ITEMS: NavItem[] = [
  { id: 'today', label: 'Today', icon: 'today', perm: null, section: 'Overview', base: true },
  { id: 'smart_inbox', label: 'Smart Inbox', icon: 'inbox', perm: 'perm-view-inbox', section: 'Overview' },
  { id: 'sourced', label: 'Autograb Sourcing', icon: 'search', perm: null, section: 'Operations', base: true },
  { id: 'pipeline', label: 'Deal Pipeline', icon: 'pipeline', perm: null, section: 'Operations', base: true },
  { id: 'contracts', label: 'Purchase Details (Workspace)', icon: 'contracts', perm: null, section: 'Operations', base: true },
  // The outflow mirror of Purchase Details: where a deal_pending sale is finished.
  { id: 'sale_details', label: 'Sale Details (Workspace)', icon: 'receipt', perm: null, section: 'Operations', base: true },
  // Winnie's review: DD Review sits above Stock Management.
  { id: 'dd_review', label: 'DD Review', icon: 'shield', perm: 'perm-review-dd', section: 'Operations' },
  { id: 'stock', label: 'Stock Management', icon: 'car', perm: null, section: 'Operations', base: true },
  { id: 'sms_log', label: 'SMS Log', icon: 'message', perm: null, section: 'Operations', base: true },
  // Winnie, 13 Sep: "Move Cost of Sales Navigation to the Finance & Insights section."
  // It was under Stock Management in Operations (her v8 note); costs entered here are
  // what Payment Run pays and what every margin figure downstream is struck against,
  // so it now opens the finance block rather than closing the operations one.
  { id: 'cost_of_sales', label: 'Cost of Sales', icon: 'receipt', perm: null, section: 'Finance & Insights', base: true },
  { id: 'payment_run', label: 'Payment Run (AP)', icon: 'card', perm: 'perm-1st-approval', section: 'Finance & Insights' },
  { id: 'payment_approval', label: 'Payment Approval', icon: 'check', perm: 'perm-2nd-release', section: 'Finance & Insights' },
  { id: 'gst_register', label: 'Margin Scheme (GST Register)', icon: 'receipt', perm: 'perm-bas-signoff', section: 'Finance & Insights' },
    // Winnie's v9 note: the sales AR as a table in the sidebar, under the margin scheme.
    { id: 'sales_ar', label: 'Sales (AR)', icon: 'coins', perm: null, section: 'Finance & Insights', base: true },
  { id: 'ar_reminders', label: 'AR Reminders', icon: 'bell', perm: 'perm-manage-collections', section: 'Finance & Insights' },
  { id: 'reports', label: 'Reports', icon: 'chart', perm: 'perm-view-reports', section: 'Finance & Insights' },
  // Winnie's 7 Sep spec, section 5. It reads the same cost data the reports do, so it
  // belongs beside them rather than in Admin with the commission *settings*.
  { id: 'commission_recon', label: 'Commission Report', icon: 'coins', perm: 'perm-view-reports', section: 'Finance & Insights' },
  // Winnie's review: the Admin section reads Admin, then Commissions Settings,
  // then Feedback. Commissions moved here out of Finance & Insights.
  { id: 'settings', label: 'Admin', icon: 'gear', perm: 'perm-user-management', section: 'Admin' },
  { id: 'commission_admin', label: 'Commissions Settings', icon: 'coins', perm: 'perm-config-rates', section: 'Admin' },
  // Winnie, 13 Sep: "Move the Standard Costs Navigation to the Admin section."
  // It is a rate card someone sets, not a report someone reads, so it sits with the
  // other settings screens rather than beside the Commission Report it feeds.
  { id: 'standard_costs', label: 'Standard Costs', icon: 'coins', perm: 'perm-view-reports', section: 'Admin' },
  { id: 'feedback', label: 'Feedback', icon: 'message', perm: 'perm-user-management', section: 'Admin' },
]

export default function Sidebar({ currentView, onViewChange, user, onLogout, isOpen }: SidebarProps) {
  const [prefs, setPrefs] = useState<NavPreferences>(() => navPreferencesService.readLocal(user.id))
  const [customising, setCustomising] = useState(false)
  const [dragId, setDragId] = useState<string | null>(null)
  const [dropIndex, setDropIndex] = useState<number | null>(null)
  const [saveNote, setSaveNote] = useState<string | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)

  // The local copy renders instantly; the profile copy is authoritative and
  // arrives a moment later so the layout follows the user between machines.
  useEffect(() => {
    let cancelled = false
    navPreferencesService.load(user.id).then((remote) => {
      if (!cancelled && remote) setPrefs(remote)
    })
    return () => {
      cancelled = true
    }
  }, [user.id])

  const canSee = useCallback(
    (item: NavItem) =>
      item.base ||
      isSuperAdmin(user) ||
      (item.perm ? hasPermission(user, item.perm) : user.role === 'admin'),
    [user]
  )

  /** Everything this user is allowed to see, in their chosen order. */
  const permitted = useMemo(
    () => applyNavOrder(NAV_ITEMS.filter(canSee), prefs.order),
    [canSee, prefs.order]
  )

  const hiddenSet = useMemo(() => new Set(prefs.hidden), [prefs.hidden])
  const visible = useMemo(() => permitted.filter((i) => !hiddenSet.has(i.id)), [permitted, hiddenSet])

  const persist = useCallback(
    async (next: NavPreferences) => {
      setPrefs(next)
      const ok = await navPreferencesService.save(user.id, next)
      /* The layout is a save like any other; "Saved on this device only" is a different
         fact and keeps its own words. */
      if (ok) toast.saved()
      setSaveNote(ok ? null : 'Saved on this device only')
      window.setTimeout(() => setSaveNote(null), 2200)
    },
    [user.id]
  )

  const reorder = (from: number, to: number) => {
    if (from === to || to < 0 || to >= permitted.length) return
    const ids = permitted.map((i) => i.id)
    const [moved] = ids.splice(from, 1)
    ids.splice(to, 0, moved)
    persist({ ...prefs, order: ids })
  }

  const toggleHidden = (id: string) => {
    const hidden = hiddenSet.has(id) ? prefs.hidden.filter((h) => h !== id) : [...prefs.hidden, id]
    // Keep an explicit order once the user starts customising, so a later
    // change to the default list can't silently reshuffle what they set up.
    persist({ ...prefs, hidden, order: prefs.order.length ? prefs.order : permitted.map((i) => i.id) })
  }

  const resetLayout = () => {
    setPrefs(emptyNavPreferences())
    navPreferencesService.reset(user.id).then((ok) => {
      if (ok) toast.saved()
      setSaveNote(ok ? 'Reset to the default layout' : 'Reset on this device only')
      window.setTimeout(() => setSaveNote(null), 2200)
    })
  }

  /* --- Dragging -----------------------------------------------------------
     Pointer events rather than HTML5 drag-and-drop, which does not fire from
     touch — the same mistake that makes the Deal Pipeline board unusable on a
     tablet. Keyboard reordering is available on every row as well.          */

  const onPointerDown = (e: React.PointerEvent, id: string, index: number) => {
    if (!customising) return
    e.preventDefault()
    ;(e.target as HTMLElement).setPointerCapture?.(e.pointerId)
    setDragId(id)
    setDropIndex(index)
  }

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragId || !listRef.current) return
    const rows = Array.from(listRef.current.querySelectorAll<HTMLElement>('[data-nav-row]'))
    const y = e.clientY
    let next = rows.length - 1
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i].getBoundingClientRect()
      if (y < r.top + r.height / 2) {
        next = i
        break
      }
    }
    setDropIndex(next)
  }

  const onPointerUp = () => {
    if (dragId && dropIndex !== null) {
      const from = permitted.findIndex((i) => i.id === dragId)
      if (from !== -1) reorder(from, dropIndex)
    }
    setDragId(null)
    setDropIndex(null)
  }

  const onRowKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (!customising) return
    if (e.key === 'ArrowUp' && (e.metaKey || e.ctrlKey || e.altKey)) {
      e.preventDefault()
      reorder(index, index - 1)
    } else if (e.key === 'ArrowDown' && (e.metaKey || e.ctrlKey || e.altKey)) {
      e.preventDefault()
      reorder(index, index + 1)
    }
  }

  const renderNav = () => {
    if (customising) {
      return (
        <div className="nav-customise" ref={listRef} onPointerMove={onPointerMove} onPointerUp={onPointerUp}>
          <p className="customise-hint">
            Drag a row to reorder it, or hold ⌥/Ctrl and press ↑ / ↓. Untick anything you don’t use.
          </p>
          {permitted.map((item, index) => (
            <div
              key={item.id}
              data-nav-row
              className={[
                'nav-row',
                dragId === item.id ? 'dragging' : '',
                dropIndex === index && dragId && dragId !== item.id ? 'drop-target' : '',
                hiddenSet.has(item.id) ? 'is-hidden' : '',
              ].join(' ')}
              tabIndex={0}
              onKeyDown={(e) => onRowKeyDown(e, index)}
              aria-label={`${item.label}, position ${index + 1} of ${permitted.length}`}
            >
              <span
                className="nav-grip"
                title="Drag to reorder"
                aria-hidden="true"
                onPointerDown={(e) => onPointerDown(e, item.id, index)}
              >
                ⠿
              </span>
              <span className="icon">{ICONS[item.icon]}</span>
              <span className="label">{item.label}</span>
              <label className="nav-visible-toggle" title={hiddenSet.has(item.id) ? 'Show in the sidebar' : 'Hide from the sidebar'}>
                <input
                  type="checkbox"
                  checked={!hiddenSet.has(item.id)}
                  onChange={() => toggleHidden(item.id)}
                />
              </label>
            </div>
          ))}

          <div className="customise-actions">
            <button className="customise-btn" onClick={resetLayout}>Reset to default</button>
            <button className="customise-btn primary" onClick={() => setCustomising(false)}>Done</button>
          </div>
        </div>
      )
    }

    // Section headings are kept, but only where the user's order hasn't
    // interleaved sections — otherwise the same heading would repeat down the
    // list, which reads as a bug rather than as grouping.
    const sectionsIntact = visible.every((item, i, arr) => {
      const first = arr.findIndex((x) => x.section === item.section)
      const last = arr.map((x) => x.section).lastIndexOf(item.section)
      return i >= first && i <= last
    })

    let lastSection: string | null = null
    return (
      <>
        {visible.map((item) => {
          const showHeading = sectionsIntact && item.section !== lastSection
          lastSection = item.section
          return (
            <div key={item.id}>
              {showHeading && (
                <div className="nav-divider">
                  <span className="divider-label">{item.section}</span>
                </div>
              )}
              <button
                className={`nav-item ${currentView === item.id ? 'active' : ''}`}
                onClick={() => onViewChange(item.id)}
              >
                <span className="icon">{ICONS[item.icon]}</span>
                <span className="label">{item.label}</span>
              </button>
            </div>
          )
        })}
        {visible.length === 0 && (
          <p className="customise-hint">
            Every item is hidden. Use “Customise” below to bring some back.
          </p>
        )}
      </>
    )
  }

  return (
    <aside className={`sidebar ${isOpen ? 'open' : 'closed'}`}>
      <div className="sidebar-header">
        <div className="logo">DA</div>
        <h2>DirectAuto</h2>
        {/* The app's top header is hidden on desktop, so the bell lives here. */}
        <NotificationBell onNavigate={onViewChange} />
      </div>

      <nav className="sidebar-nav">{renderNav()}</nav>

      <div className="sidebar-footer">
        {saveNote && <div className="nav-save-note">{saveNote}</div>}
        <button
          className="customise-toggle"
          onClick={() => setCustomising((v) => !v)}
          aria-pressed={customising}
        >
          {customising ? 'Done customising' : 'Customise menu'}
        </button>
        <div className="user-info">
          <div className="user-avatar">{user.full_name?.charAt(0) || '?'}</div>
          <div className="user-details">
            <div className="user-name">{user.full_name}</div>
            <div className="user-role">{user.role}</div>
          </div>
        </div>
        <button className="logout-button" onClick={onLogout}>
          Logout
        </button>
      </div>
    </aside>
  )
}
